import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderRich(text) {
  // Render **bold** and [label](url) markdown into JSX
  const parts = [];
  let key = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIdx = 0;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push({ type: "text", value: text.slice(lastIdx, m.index) });
    parts.push({ type: "link", label: m[1], url: m[2] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push({ type: "text", value: text.slice(lastIdx) });

  return parts.map((p) => {
    key += 1;
    if (p.type === "link") {
      const isExternal = /^https?:\/\//.test(p.url);
      return (
        <a
          key={key}
          href={p.url}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noreferrer" : undefined}
          data-testid="chat-msg-link"
          className="text-[var(--gold)] underline decoration-[var(--gold)]/40 underline-offset-2 hover:text-[var(--navy-900)] hover:decoration-[var(--navy-900)] font-semibold"
        >
          {p.label}
        </a>
      );
    }
    // bold inside text
    const segments = p.value.split(/(\*\*[^*]+\*\*)/g);
    return segments.map((seg, i) => {
      const k = `${key}-${i}`;
      if (/^\*\*[^*]+\*\*$/.test(seg)) {
        return (
          <strong key={k} className="font-semibold text-[var(--navy-900)]">
            {seg.slice(2, -2)}
          </strong>
        );
      }
      return <span key={k}>{seg}</span>;
    });
  });
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => `sess-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "I'm Ask A.L.I.G.N. — share what's not working in your organization and I'll map it to the right pillars." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const bodyRef = useRef(null);

  // When rendered inside the /embed iframe (Wix host), we use a compact
  // icon-only bubble so it fits comfortably inside a small iframe wrapper
  // and never gets clipped or text-wrapped by host-side sizing constraints.
  const isEmbed = typeof window !== "undefined" && window.location.pathname.startsWith("/embed");

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  // Subtle engagement nudge — appears after 8s of idle if the user hasn't
  // opened the chat yet. Dismissed forever once they engage.
  useEffect(() => {
    if (open) {
      setShowNudge(false);
      return;
    }
    const t = setTimeout(() => setShowNudge(true), 8000);
    return () => clearTimeout(t);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        aria-label="Open Ask A.L.I.G.N."
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-6 right-6 z-50 group flex items-center gap-3 ${isEmbed ? "p-2" : "pl-4 pr-5 py-3"} rounded-full bg-[var(--navy-950)] text-[var(--bone)] shadow-xl shadow-[rgba(7,20,43,0.45)] border border-[var(--gold)]/60 hover:bg-[var(--navy-800)] hover:scale-[1.03] transition-all duration-300 ${open ? "scale-90 opacity-0 pointer-events-none" : ""}`}
      >
        <span className="relative flex w-12 h-12 rounded-full bg-[var(--gold)] items-center justify-center text-[var(--navy-950)]">
          <MessageCircle size={22} />
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[var(--gold-soft)] ring-2 ring-[var(--navy-950)] animate-pulse"></span>
          {showNudge && (
            <span
              data-testid="chat-nudge-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--gold)] text-[var(--navy-950)] text-[10px] font-bold flex items-center justify-center ring-2 ring-[var(--navy-950)] shadow-lg animate-bounce"
            >
              1
            </span>
          )}
        </span>
        {!isEmbed && (
          <span className="text-left leading-tight">
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[var(--gold-soft)] font-bold">Ask A.L.I.G.N.</span>
            <span className="block text-[12px] text-[var(--bone)]/90">Conversational diagnostic</span>
          </span>
        )}
      </button>

      {open && (
        <button
          data-testid="chat-close"
          aria-label="Close chat"
          onClick={() => setOpen(false)}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[var(--navy-950)] text-[var(--gold-soft)] shadow-xl border border-[var(--gold)]/60 flex items-center justify-center hover:bg-[var(--navy-800)] transition-colors"
        >
          <X size={20} />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-[400px] h-[520px] max-h-[70vh] bg-[var(--bone)] border border-[var(--navy-900)]/15 shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-[var(--navy-950)] text-[var(--bone)] p-5 border-b border-[var(--gold)]/20">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold-soft)] font-bold">Ask A.L.I.G.N.™</div>
            <div className="font-display text-[20px] mt-1">Conversational diagnostic</div>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((m, i) => (
              <div
                key={`${i}-${m.role}-${(m.text || "").slice(0, 24)}`}
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] text-[14px] leading-relaxed p-3 ${
                  m.role === "user"
                    ? "ml-auto bg-[var(--navy-900)] text-[var(--bone)]"
                    : "bg-white border border-[var(--navy-900)]/10 text-[var(--navy-900)]"
                }`}
                style={{ whiteSpace: "pre-wrap" }}
              >
                {m.role === "assistant" ? renderRich(m.text) : m.text}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-[var(--muted)] text-[13px]">
                <Loader2 size={14} className="animate-spin" /> thinking…
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[var(--navy-900)]/10 bg-white">
            <Link
              to="/assessment"
              data-testid="chat-cta-assessment"
              className="block text-center text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--gold)] hover:text-[var(--navy-900)] mb-3"
              onClick={() => setOpen(false)}
            >
              Take the 5-min A.L.I.G.N. Readiness Index →
            </Link>
            <div className="flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Describe a friction point…"
                className="flex-1 px-3 py-2.5 bg-[var(--navy-50)] border border-[var(--navy-900)]/10 outline-none text-[14px] text-[var(--navy-900)] focus:border-[var(--navy-900)]"
              />
              <button
                data-testid="chat-send"
                onClick={send}
                disabled={loading || !input.trim()}
                className="bg-[var(--navy-900)] text-[var(--bone)] px-4 disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
